import React from 'react'
import { Layout } from '@design-systems/canvas-ui'
import type {
  IMenuItem,
  PrimaryMenuItem,
  SecondaryMenuItem,
} from '@design-systems/canvas-ui'
import { useLocation, useNavigate } from 'react-router-dom'
import { useEntitlements, useAnalytics, useAuth } from 'properties-ui-common'
import { TgtMeter } from '@measurement/opentelemetry-web'
import { sideNavigationArray } from './SideNavigationItems'
import { NAV_ICON_MAP } from './SideNavIcons'
import {
  POL_ITEM_EXTERNAL_TAKEOFF_BASE,
  POL_QMS_VENDOR_TAKEOFF_BASE,
} from '../../../MicroFrontEnds/utility/constants'

const buttonClickCounter = TgtMeter.createCounter(
  'skylight_side_navigation_button_clicks',
  {
    description:
      'Counts the number of times the side navigation hamburger button is clicked in skylight UI',
  },
)

/**
 * Derive a stable Canvas 8 `id` from a nav item.
 * Leaf items (with `path`) use the path directly so `onChange` can navigate to it.
 * Group items (no path, only children) get a slug derived from their label.
 */
const toItemId = (item: any): string =>
  item.path ??
  String(item.label)
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')

const isLeafActive = (pathname: string, path?: string): boolean => {
  if (!path || path.startsWith('http')) return false
  if (path === '/') return pathname === '/'
  return pathname.startsWith(path)
}

export const SideNavigation: React.FC = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { entitlements } = useEntitlements()
  const { sendEvent } = useAnalytics()
  const auth = useAuth()
  const lanId = auth?.session?.userInfo?.lanId ?? ''
  const isPolUser = entitlements?.pol?.read
  const isQmsPolUser = entitlements?.qms?.pol
  const isPolNriUser = entitlements?.nri?.pol
  const isItemExternalTakeoffPath = window.location.hostname.includes(
    POL_ITEM_EXTERNAL_TAKEOFF_BASE,
  )
  const isQmsVendorTakeoffPath = window.location.hostname.includes(
    POL_QMS_VENDOR_TAKEOFF_BASE,
  )

  const sideNavItems = sideNavigationArray(entitlements)

  // ── POL access logic — preserved verbatim from the original Sidenavigation.tsx ──

  const getPolAccessKeys = (): string[] => {
    // If new is...Allowed keys are added, they can be added and handled here...
    const keys: string[] = []
    if (isPolUser) keys.push('isPolAllowed')
    if (isQmsVendorTakeoffPath && isQmsPolUser) keys.push('isQmsPolAllowed')
    if (isItemExternalTakeoffPath && isPolNriUser) keys.push('isPolNriAllowed')
    return keys // If no relevant user types, allow access by default
  }

  const hasAnyAccess = (val: any, keys: string[]): boolean =>
    keys.some((key) => !!val?.[key])

  const allChildrenNoAccess = (val: any, keys: string[]): boolean =>
    val?.children?.every((child: any) => keys.every((key) => !child?.[key])) ??
    true

  const checkIfPolAndNoAccess = (val: any): boolean => {
    const accessKeys = getPolAccessKeys()

    // If no relevant user types, allow access by default
    if (accessKeys.length === 0) return false

    // If the item has access for any of the user types, allow it
    if (hasAnyAccess(val, accessKeys)) return false

    // Otherwise, deny if all children also have no access
    return allChildrenNoAccess(val, accessKeys)
  }

  // ── Transform SideNavigationItems array to Canvas 8 PrimaryMenuItem[] ──
  // The only thing that changes from Canvas 7: instead of rendering JSX in a loop,
  // we build a PrimaryMenuItem[] and pass it to Layout.SideNav.
  // The hide conditions are identical to the original navItem() render function:
  //   checkIfPolAndNoAccess(item) || (visibleForEntitledOnly && !entitlements)

  const navItems = sideNavItems.reduce<PrimaryMenuItem[]>((acc, item: any) => {
    if (checkIfPolAndNoAccess(item)) return acc

    if (item.children) {
      const visibleChildren = item.children
        .filter(
          (child: any) =>
            !checkIfPolAndNoAccess(child) &&
            !(child?.visibleForEntitledOnly && !child?.entitlements),
        )
        .map(
          (child: any): IMenuItem => ({
            id: toItemId(child),
            label: child.label,
            isActive: isLeafActive(pathname, child.path),
          }),
        )

      // If all children are hidden, skip the group entirely
      if (visibleChildren.length === 0) return acc

      acc.push({
        id: toItemId(item),
        label: item.label,
        icon: NAV_ICON_MAP[item.label],
        isActive: visibleChildren.some((c: IMenuItem) => c.isActive),
        secondaryItems: visibleChildren as SecondaryMenuItem[],
      })
    } else {
      // Hide side nav item for POL users if they don't have access, and also
      // if the side nav item should be hidden when the required entitlement is absent
      if (!(item?.visibleForEntitledOnly && !item?.entitlements)) {
        acc.push({
          id: toItemId(item),
          label: item.label,
          icon: NAV_ICON_MAP[item.label],
          isActive: isLeafActive(pathname, item.path),
        })
      }
    }

    return acc
  }, [])

  const handleChange = (item: IMenuItem) => {
    const path = item.id
    buttonClickCounter.add(1, { button: path })
    sendEvent({
      event: { type: 'CLICK', value: item.label as string, id: path },
      //@ts-ignore
      client: {
        employee_login_id: lanId,
      },
    })
    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer')
    } else if (path.startsWith('/')) {
      navigate(path)
    }
    // Group items have slug IDs (no leading '/') — clicking them expands/collapses,
    // no navigation needed
  }

  return (
    <Layout.SideNav
      data-testid="skylight-side-nav"
      items={navItems}
      onChange={handleChange}
    />
  )
}
